import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  RefreshCw,
  UserCheck,
  Phone,
  Mail,
  DollarSign,
  Filter,
  X,
  Grid3x3,
  List,
  Grid2x2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { getReferrers, getReferrerStatistics, deleteReferrer } from '../api/referrers';
import Pagination from '../components/common/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';

const ReferrersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);

  // View mode state
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('referrerViewMode') || 'grid';
  });

  // Save view mode to localStorage
  useEffect(() => {
    localStorage.setItem('referrerViewMode', viewMode);
  }, [viewMode]);

  // Filter state
  const [filters, setFilters] = useState({
    page: 1,
    limit: 12,
    search: '',
    isActive: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
    hasMore: false
  });

  const [showFilters, setShowFilters] = useState(false);

  // Fetch referrers
  const fetchReferrers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getReferrers(filters);
      setReferrers(response.referrers || []);
      setPagination(
        response.pagination || {
          page: 1,
          limit: 12,
          total: 0,
          totalPages: 0,
          hasMore: false
        }
      );
    } catch (error) {
      toast.error('Failed to fetch referrers');
      setReferrers([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const stats = await getReferrerStatistics();
      setStatistics(stats);
    } catch (error) {}
  }, []);

  useEffect(() => {
    fetchReferrers();
    fetchStatistics();
  }, [fetchReferrers, fetchStatistics]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 12,
      search: '',
      isActive: '',
      sortBy: 'created_at',
      sortOrder: 'DESC'
    });
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this referrer?')) {
      return;
    }

    try {
      await deleteReferrer(id);
      toast.success('Referrer deleted successfully');
      fetchReferrers();
      fetchStatistics();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete referrer';
      toast.error(errorMessage);
    }
  };

  // Check if user can edit
  const canEdit = [
    'admin',
    'super_admin',
    'retail_admin',
    'branch_admin',
    'staff',
    'branch_staff'
  ].includes(user?.role);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary-black">
                Referrer Management
              </h1>
              <p className="text-sm md:text-base text-gray-600 mt-1">
                Manage referrers and track commissions
              </p>
            </div>
            {canEdit && (
              <button
                onClick={() => navigate('/referrers/new')}
                className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors min-h-[44px]"
              >
                <Plus className="w-5 h-5" />
                Add Referrer
              </button>
            )}
          </div>

          {/* Statistics */}
          {statistics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-4 border border-blue-500">
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                  Total Referrers
                </h3>
                <p className="text-xl md:text-2xl font-bold text-primary-black">
                  {statistics.total_referrers}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-green-500">
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">Active</h3>
                <p className="text-xl md:text-2xl font-bold text-primary-black">
                  {statistics.active_referrers}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-purple-500">
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">
                  Unpaid Commission
                </h3>
                <p className="text-xl md:text-2xl font-bold text-primary-black">
                  {formatCurrency(statistics.total_unpaid_commissions || 0)}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-accent-gold">
                <h3 className="text-xs md:text-sm font-medium text-gray-600 mb-1">Total Earned</h3>
                <p className="text-xl md:text-2xl font-bold text-primary-black">
                  {formatCurrency(statistics.total_commissions_earned || 0)}
                </p>
              </div>
            </div>
          )}

          {/* Search, Filter and View Mode Bar */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex flex-col md:flex-row gap-3 md:gap-4">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by name, phone, email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                />
              </div>
              <div className="flex gap-2">
                {/* View Mode Toggle */}
                <div className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1 border border-gray-300 rounded-lg bg-gray-50">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1 sm:p-1.5 rounded transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center ${
                      viewMode === 'list'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="List view"
                  >
                    <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('compact')}
                    className={`p-1 sm:p-1.5 rounded transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center ${
                      viewMode === 'compact'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Compact view"
                  >
                    <Grid2x2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1 sm:p-1.5 rounded transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center ${
                      viewMode === 'grid'
                        ? 'bg-accent-gold text-primary-black'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Grid view"
                  >
                    <Grid3x3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[36px]"
                >
                  <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Filters</span>
                </button>
                <button
                  onClick={() => {
                    fetchReferrers();
                    fetchStatistics();
                  }}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[36px]"
                >
                  <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={filters.isActive}
                      onChange={(e) => handleFilterChange('isActive', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="">All Status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                    <select
                      value={filters.sortBy}
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="created_at">Date Created</option>
                      <option value="full_name">Name</option>
                      <option value="total_referrals">Total Referrals</option>
                      <option value="commission_balance">Commission Balance</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sort Order
                    </label>
                    <select
                      value={filters.sortOrder}
                      onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                    >
                      <option value="DESC">Descending</option>
                      <option value="ASC">Ascending</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                >
                  <X className="w-4 h-4" />
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Referrers Display */}
        {loading ? (
          <LoadingSpinner message="Loading referrers..." className="py-20" />
        ) : referrers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <UserCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Referrers Found</h3>
            <p className="text-gray-500 mb-6">
              {filters.search
                ? 'Try adjusting your search'
                : 'Get started by adding your first referrer'}
            </p>
            {canEdit && !filters.search && (
              <button
                onClick={() => navigate('/referrers/new')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Referrer
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {referrers.map((referrer) => (
                  <div
                    key={referrer.id}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/referrers/${referrer.id}`)}
                  >
                    <div className="p-4">
                      {/* Referrer Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-primary-black mb-1">
                            {referrer.full_name}
                          </h3>
                          <p className="text-xs text-gray-500">{referrer.referrer_code}</p>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded ${
                            referrer.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {referrer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-2 mb-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{referrer.phone}</span>
                        </div>
                        {referrer.email && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span className="truncate">{referrer.email}</span>
                          </div>
                        )}
                      </div>

                      {/* Commission Info */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-600">Commission Rate</span>
                          <span className="text-sm font-bold text-accent-gold">
                            {referrer.commission_type === 'percentage'
                              ? `${referrer.commission_rate}%`
                              : formatCurrency(referrer.fixed_commission_amount || 0)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-600">Total Referrals</span>
                          <span className="text-sm font-bold text-blue-600">
                            {referrer.total_referrals || 0}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">Unpaid Balance</span>
                          <span className="text-sm font-bold text-yellow-600">
                            {formatCurrency(referrer.commission_balance || 0)}
                          </span>
                        </div>
                      </div>

                      {/* Financial Summary */}
                      <div className="flex items-center justify-between mb-3 pb-3 border-b text-sm">
                        <div>
                          <p className="text-xs text-gray-500">Total Earned</p>
                          <p className="font-bold text-green-600">
                            {formatCurrency(referrer.total_commission_earned || 0)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Total Paid</p>
                          <p className="font-bold text-gray-700">
                            {formatCurrency(referrer.total_commission_paid || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Compact View */}
            {viewMode === 'compact' && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {referrers.map((referrer) => (
                  <div
                    key={referrer.id}
                    className="bg-white rounded-lg shadow-md p-3 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/referrers/${referrer.id}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-primary-black truncate">
                        {referrer.full_name}
                      </h3>
                      <span
                        className={`px-1 py-0.5 text-xs rounded flex-shrink-0 ml-1 ${
                          referrer.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {referrer.is_active ? 'A' : 'I'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{referrer.phone}</p>
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <p className="text-xs text-gray-500">Referrals</p>
                        <p className="text-xs font-bold text-blue-600">
                          {referrer.total_referrals || 0}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Rate</p>
                        <p className="text-xs font-bold text-accent-gold">
                          {referrer.commission_type === 'percentage'
                            ? `${referrer.commission_rate}%`
                            : 'Fixed'}
                        </p>
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <p className="text-xs text-gray-500">Unpaid</p>
                      <p className="text-xs font-bold text-yellow-600">
                        {formatCurrency(referrer.commission_balance || 0)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <>
                {/* Mobile Card View */}
                <div className="block md:hidden space-y-3">
                  {referrers.map((referrer) => (
                    <div key={referrer.id} className="bg-white rounded-lg shadow-md p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="h-12 w-12 flex-shrink-0 bg-accent-gold rounded-full flex items-center justify-center">
                          <UserCheck className="w-6 h-6 text-primary-black" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 mb-1">
                            {referrer.full_name}
                          </h3>
                          <div className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-gradient-to-r from-accent-gold to-accent-goldDark text-primary-black inline-block">
                            {referrer.referrer_code}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-3 pb-3 border-b border-gray-100">
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Phone</p>
                          <p className="text-sm font-medium text-gray-900">{referrer.phone}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Status</p>
                          <span
                            className={`px-2 py-0.5 text-xs font-semibold rounded ${
                              referrer.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {referrer.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Total Referrals</p>
                          <p className="text-sm font-bold text-blue-600">
                            {referrer.total_referrals || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Commission Rate</p>
                          <p className="text-sm font-bold text-accent-gold">
                            {referrer.commission_type === 'percentage'
                              ? `${referrer.commission_rate}%`
                              : formatCurrency(referrer.fixed_commission_amount || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Unpaid Balance</p>
                          <p className="text-sm font-bold text-yellow-600">
                            {formatCurrency(referrer.commission_balance || 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Total Earned</p>
                          <p className="text-sm font-bold text-green-600">
                            {formatCurrency(referrer.total_commission_earned || 0)}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/referrers/${referrer.id}`)}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Referrer
                          </th>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-4 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Commission
                          </th>
                          <th className="px-4 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Referrals
                          </th>
                          <th className="px-4 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Unpaid
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {referrers.map((referrer) => (
                          <tr
                            key={referrer.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate(`/referrers/${referrer.id}`)}
                          >
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <div>
                                <p className="text-sm font-semibold text-primary-black">
                                  {referrer.full_name}
                                </p>
                                <p className="text-xs text-gray-500">{referrer.referrer_code}</p>
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <div className="text-sm">
                                <p className="text-gray-900">{referrer.phone}</p>
                                {referrer.email && (
                                  <p className="text-xs text-gray-500 truncate max-w-[150px] lg:max-w-xs">
                                    {referrer.email}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 text-xs font-semibold rounded ${
                                  referrer.is_active
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {referrer.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-right">
                              <span className="text-sm font-semibold text-accent-gold">
                                {referrer.commission_type === 'percentage'
                                  ? `${referrer.commission_rate}%`
                                  : formatCurrency(referrer.fixed_commission_amount || 0)}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-right">
                              <span className="text-sm font-semibold text-blue-600">
                                {referrer.total_referrals || 0}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-right">
                              <span className="text-sm font-semibold text-yellow-600">
                                {formatCurrency(referrer.commission_balance || 0)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* Pagination */}
            <div className="mt-6">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                itemsPerPage={filters.limit}
                onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
                itemLabel="referrers"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReferrersPage;
